import { ClientOnly } from '@/ClientOnly';
import { HostedVideoPoster } from '@/podcastVideoElement/HostedVideoPoster';
import type { DreamRenderElementProps } from '@/types';
import { TooltipWrapper } from '@/ui/TooltipWrapper';
import { cn } from '@/utils';

import * as styles from './AudioPlayer.css';
import type { AudioPlayerProps, TAudioPlayerElement } from './AudioPlayer.type';
import { PLAYER_MODE } from './AudioPlayer.type';
import { ClassicPlayer } from './ClassicPlayer';
import { DetailedPlayer } from './DetailedPlayer';
import { resolveOptions } from './presets';
import { WaveformPlayer } from './WaveformPlayer';

const PLAYER_COMPONENTS = {
  [PLAYER_MODE.CLASSIC]: ClassicPlayer,
  [PLAYER_MODE.WAVEFORM]: WaveformPlayer,
  [PLAYER_MODE.DETAILED]: DetailedPlayer,
};

export function AudioPlayerElement({
  subtitle,
  audioUrl,
  audioType,
  thumbnailUrl,
  title,
  waveform,
  duration,
  isLive,
  options,
  className,
  onPlayAttempt,
  tooltipText,
  isPlaybackLocked,
  isPreview,
  audioElementId,
  playbackStorageKey,
}: AudioPlayerProps) {
  const resolved = resolveOptions(options);
  const Player = PLAYER_COMPONENTS[resolved.playerMode] ?? WaveformPlayer;
  const dataProps = {
    subtitle,
    audioUrl,
    audioType,
    thumbnailUrl,
    title,
    waveform,
    duration,
    isLive,
    onPlayAttempt,
    isPreview,
    audioElementId,
    playbackStorageKey,
  };

  // NOTE: audioUrl is passed explicitly due to a corrupt template error,
  // which was fixed but some pages might have the old template.
  return (
    <TooltipWrapper
      portalId="audio-player-container"
      tooltipText={tooltipText || ''}
      shouldMount={!!tooltipText}
      isTriggerAsChild
    >
      <div className={cn(styles.container, 'flex-none h-fit', className)}>
        {resolved.playerMode === PLAYER_MODE.CLASSIC ? (
          <ClassicPlayer {...dataProps} {...resolved} audioUrl={audioUrl} isPlaybackLocked={isPlaybackLocked} />
        ) : (
          <ClientOnly
            fallback={
              <ClassicPlayer {...dataProps} {...resolved} audioUrl={audioUrl} isPlaybackLocked={isPlaybackLocked} />
            }
          >
            {() => <Player {...dataProps} {...resolved} audioUrl={audioUrl} isPlaybackLocked={isPlaybackLocked} />}
          </ClientOnly>
        )}
      </div>
    </TooltipWrapper>
  );
}

/** Serializer-compatible wrapper that maps TipTap node attrs to AudioPlayerElement props */
export function AudioPlayerSerializer({
  element,
  className,
  onPlayAttempt,
  tooltipText,
  isPlaybackLocked,
  isPreview: isPreviewProp,
  playbackStorageKey,
}: DreamRenderElementProps<TAudioPlayerElement> & {
  onPlayAttempt?: () => void;
  tooltipText?: string;
  isPlaybackLocked?: boolean;
  isPreview?: boolean;
  playbackStorageKey?: string;
}) {
  const { id: nodeId, data, isLive = true, ...options } = element.attrs || {};

  const audioUrl = data?.audioUrl && typeof data.audioUrl === 'string' ? data.audioUrl : '';
  const hasAudio = audioUrl.length > 0;
  const audioId = nodeId || element.attrs?.mergeTag;
  const isPreview = isPreviewProp ?? Boolean(data?.isPreview);

  const hostedVideo = data?.hostedVideo;
  const videoLink = (() => {
    if (!hostedVideo && typeof data?.youtubeVideoId === 'string') {
      return `https://www.youtube.com/embed/${data.youtubeVideoId}`;
    }

    return null;
  })();

  const renderedAudioPlayer = (
    <AudioPlayerElement
      audioUrl={audioUrl}
      audioType={typeof data?.audioType === 'string' ? data?.audioType : undefined}
      thumbnailUrl={typeof data?.thumbnailUrl === 'string' ? data?.thumbnailUrl : undefined}
      title={typeof data?.title === 'string' ? data?.title : undefined}
      waveform={data?.waveform}
      duration={typeof data?.duration === 'number' ? data?.duration : undefined}
      isLive={isLive}
      options={options}
      className={className}
      onPlayAttempt={!hasAudio ? onPlayAttempt : undefined}
      tooltipText={tooltipText}
      isPlaybackLocked={isPlaybackLocked}
      isPreview={isPreview}
      audioElementId={audioId ? `audio-${audioId}` : undefined}
      playbackStorageKey={playbackStorageKey}
    />
  );

  if (hostedVideo) {
    return (
      <div className="flex flex-col w-full" style={{ gap: 'var(--gap, 16px)' }}>
        <HostedVideoPoster posterUrl={hostedVideo.posterUrl} />
        {renderedAudioPlayer}
      </div>
    );
  }

  if (videoLink) {
    return (
      <div
        className="flex flex-col w-full"
        style={{
          gap: 'var(--gap, 16px)',
        }}
      >
        {/* 
          When a Podcast Episode has an associated YouTube video, we render the video in a 16:9 aspect ratio
          and below it, we render the audio player.
         */}
        <iframe
          className="w-full min-w-[200px] min-h-[200px]"
          style={{ aspectRatio: '16 / 9' }}
          src={videoLink}
          title="YouTube video player"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
        {renderedAudioPlayer}
      </div>
    );
  }

  return renderedAudioPlayer;
}
